import { useScreenSize } from '@insight-health-ai/roseate/utils';
import {
  Clock,
  Flag06,
  LogOut02,
  Phone,
  User02,
  Calendar,
  ChevronLeft,
  FaceContent,
} from '@insight-health-ai/roseate/icons';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useGetCallAudio, useGetCallDetails } from 'src/hooks/call';
import { usePageHeader } from 'src/hooks/misc';
import { CallPriority, SummaryCard, TranscriptionCard } from 'src/components';
import {
  ScreenLoader,
  CallUserSentiment,
  DetailsBar,
  InsightTag,
  DetailsBarProps,
} from 'src/components';
import { format } from 'date-fns';
import { formatPhoneNumber } from 'src/utils/misc';
import { Card } from '@insight-health-ai/roseate/components';
import { EncounterSummaryType } from 'src/models';
import { usePermissionEntitlements } from '@frontegg/react';

type CallDetailsProps = {
  callId: string;
};

export const CallDetails = ({ callId }: CallDetailsProps) => {
  const { isEntitled: hasAudioReadPermission } =
    usePermissionEntitlements('calls.audio.read');
  const navigate = useNavigate();
  const { data: call, isLoading } = useGetCallDetails({ callId: callId! });
  const { data: callAudio } = useGetCallAudio(
    {
      callId: callId!,
    },
    {
      enabled: !!callId && hasAudioReadPermission,
    }
  );

  if (isLoading) {
    return <ScreenLoader />;
  }

  if (!call) {
    navigate('/phone-triage/calls');
    return null;
  }

  // TODO: add error handling

  const callDetails: DetailsBarProps['details'] = [
    [
      {
        title: 'Call Time',
        content: call.start_time && format(new Date(call.start_time), 'h:mm a'),
        icon: <Clock />,
        fallback: 'Unknown',
      },
      {
        title: 'Duration',
        content:
          call.duration &&
          (() => {
            const minutes = Math.floor(call.duration / 60);
            const seconds = Math.round(call.duration % 60);
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          })(),
        icon: <Clock />,
        fallback: 'Unknown',
      },
      {
        title: 'Call Type',
        content: (call.type || '').replace(/_/g, ' '),
        icon: <Phone />,
        fallback: 'Unknown',
        style: {
          textTransform: 'capitalize',
        },
      },
    ],
    [
      {
        title: 'Patient',
        content:
          call.call_analytics?.call_analysis?.custom_analysis_data
            ?.patient_name,
        icon: <User02 />,
        fallback: 'Unknown',
      },
      {
        title: 'Phone',
        content: call.source && formatPhoneNumber(call.source),
        icon: <Phone />,
        fallback: 'N/A',
      },
      {
        title: 'Priority',
        content: call.priority && (
          <CallPriority
            priority={call.priority}
            statusProps={{
              size: 'xs',
            }}
          />
        ),
        icon: <Flag06 />,
        fallback: 'Unknown',
        style: {
          textTransform: 'capitalize',
        },
      },
    ],
    [
      {
        title: 'Disconnect',
        content: (call.disconnect_reason || '').replace(/_/g, ' '),
        icon: <LogOut02 />,
        fallback: 'Unknown',
        style: {
          textTransform: 'capitalize',
        },
      },
      {
        title: 'Date',
        content:
          call.start_time && format(new Date(call.start_time), 'MM/dd/yyyy'),
        icon: <Calendar />,
        fallback: 'Unknown',
      },
      {
        title: 'User Sentiment',
        content: call.call_analytics?.call_analysis?.user_sentiment ? (
          <CallUserSentiment call={call} />
        ) : null,
        icon: <FaceContent />,
        fallback: 'Unknown',
      },
    ],
  ];

  return (
    <div className="md:h-full overflow-hidden">
      <div className="flex flex-col gap-3 h-full overflow-hidden">
        <DetailsBar details={callDetails} />
        {!!call.tags?.length && (
          <Card outlined={false} padding={'sm'}>
            <div className="flex gap-2">
              <div className="text-sm font-medium">Auto detected tags:</div>
              <div className="flex flex-wrap gap-2">
                {call.tags.map((tag) => (
                  <InsightTag key={tag.id} tag={tag} isSelected />
                ))}
              </div>
            </div>
          </Card>
        )}
        <div className="flex flex-col md:flex-row gap-3 h-full justify-stretch overflow-hidden">
          <SummaryCard
            filters={{
              encounterId: call?.encounter_id || '',
              // encounterPhase: data?.call?.encounter?.type as EncounterPhase,
              type: call?.encounter?.type as EncounterSummaryType,
            }}
          />
          <TranscriptionCard
            filters={{
              encounterId: call?.encounter_id || '',
              type: 'live',
            }}
            patient={call?.patient}
            title="Call Transcript"
            audio={callAudio?.url ? { src: callAudio.url } : undefined}
          />
        </div>
      </div>
    </div>
  );
};

export const CallDetailsRoute = () => {
  const { callId } = useParams<{ callId: string }>();

  if (!callId) {
    return <Navigate to="/phone-triage/calls" />;
  }

  usePageHeader({
    title: 'Call Details',
    breadcrumb: [
      {
        icon: <ChevronLeft />,
        title: 'Back to calls',
        href: '/phone-triage/calls',
      },
    ],
  });
  const { mediaMin } = useScreenSize();

  return (
    <div
      style={mediaMin('md') ? { height: 'calc(100dvh - 100px)' } : {}}
      className="page-wrapper care-connect-page overflow-hidden"
    >
      <CallDetails callId={callId!} />
    </div>
  );
};
